<template>
  <div class="container-fluid py-4">
    <div class="row">
      <div class="col-12">
        <div class="card mb-4">
          <div class="card-header pb-0 d-flex justify-content-between align-items-center">
            <div>
              <h6>Gestión de Usuarios</h6>
              <div class="btn-group btn-group-sm mt-2" role="group">
                <button 
                  type="button" 
                  class="btn"
                  :class="currentView === 'table' ? 'btn-success' : 'btn-outline-success'"
                  @click="currentView = 'table'"
                >
                  <i class="ri-table-line me-1"></i> Vista Tabla
                </button>
                <button 
                  type="button" 
                  class="btn"
                  :class="currentView === 'matrix' ? 'btn-success' : 'btn-outline-success'"
                  @click="currentView = 'matrix'"
                >
                  <i class="ri-grid-line me-1"></i> Vista Matriz
                </button>
              </div>
            </div>
            <button 
              class="btn btn-success btn-sm mb-0" 
              @click="showCreateModal = true"
            >
              <i class="ri-add-line"></i> Nuevo Usuario
            </button>
          </div>
          <div class="card-body px-0 pt-0 pb-2">
            <div v-if="error" class="alert alert-danger mx-4 my-2">
              {{ error }}
            </div>
            
            <!-- Vista de Matriz -->
            <div v-if="currentView === 'matrix'" class="matrix-view">
              <user-permissions-matrix @close="currentView = 'table'" />
            </div>
            
            <!-- Vista de Tabla -->
            <div v-else>
              <!-- Barra de búsqueda -->
              <div class="px-4 py-3">
                <div class="row align-items-center">
                  <div class="col-md-6">
                    <div class="position-relative">
                      <i class="ri-search-line search-icon"></i>
                      <input
                        type="text"
                        class="form-control search-input"
                        placeholder="Buscar por nombre o email..."
                        v-model="searchQuery"
                        @input="onSearchInput"
                      />
                      <button 
                        v-if="searchQuery" 
                        class="btn btn-link clear-button" 
                        type="button" 
                        @click="clearSearch"
                        title="Limpiar búsqueda"
                      >
                        <i class="ri-close-line"></i>
                      </button>
                    </div>
                  </div>
                  <div class="col-md-6 d-flex align-items-center justify-content-end">
                    <small class="text-muted">
                      {{ filteredUsers.length }} de {{ users.length }} usuarios
                    </small>
                  </div>
                </div>
              </div>
              
              <div class="table-responsive p-0">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                      ID
                    </th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                      Nombre
                    </th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
                      Email
                    </th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2 text-center">
                      Estado
                    </th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
                      Fecha de Creación
                    </th>
                    <th class="text-secondary opacity-7">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="loading">
                    <td colspan="6" class="text-center py-4">
                      <div class="spinner-border text-success" role="status">
                        <span class="visually-hidden">Cargando...</span>
                      </div>
                    </td>
                  </tr>
                  <tr v-for="user in filteredUsers" :key="user.id">
                    <td>
                      <div class="d-flex px-2 py-1">
                        <div class="d-flex flex-column justify-content-center">
                          <h6 class="mb-0 text-sm">{{ user.id }}</h6>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div class="d-flex px-2 py-1">
                        <div class="d-flex flex-column justify-content-center">
                          <h6 class="mb-0 text-sm">{{ user.name }}</h6>
                        </div>
                      </div>
                    </td>
                    <td>
                      <p class="text-xs font-weight-bold mb-0">{{ user.email }}</p>
                    </td>
                    <td class="align-middle text-center">
                      <el-switch
                        v-model="user.is_active"
                        active-color="#5cb85c"
                        inactive-color="#dc3545"
                        :loading="user.statusUpdating"
                        :active-text="user.is_active ? 'Activo' : ''"
                        :inactive-text="!user.is_active ? 'Inactivo' : ''"
                        @change="() => updateUserStatus(user)"
                      />
                    </td>
                    <td>
                      <p class="text-xs font-weight-bold mb-0">{{ formatDate(user.created_at) }}</p>
                    </td>
                    <td class="align-middle">
                      <div class="d-flex">
                        <button 
                          class="btn btn-link text-primary text-gradient btn-sm mb-0 me-2"
                          @click="openPermissionsModal(user)"
                          title="Gestionar permisos"
                        >
                          <i class="ri-shield-keyhole-line me-1"></i>Permisos
                        </button>
                        <button 
                          class="btn btn-link text-danger text-gradient btn-sm mb-0"
                          @click="confirmDelete(user)"
                          title="Eliminar usuario"
                        >
                          <i class="ri-delete-bin-line me-1"></i>Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="!loading && filteredUsers.length === 0 && users.length > 0">
                    <td colspan="6" class="text-center py-4">
                      No se encontraron usuarios que coincidan con "{{ searchQuery }}"
                    </td>
                  </tr>
                  <tr v-if="!loading && users.length === 0">
                    <td colspan="6" class="text-center py-4">No hay usuarios disponibles</td>
                  </tr>
                </tbody>
              </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal para crear usuario -->
    <el-dialog
      v-model="showCreateModal"
      title="Crear Nuevo Usuario"
      width="30%"
      class="create-user-modal"
    >
      <div class="text-center" v-if="creatingUser">
        <div class="spinner-border text-success" role="status">
          <span class="visually-hidden">Cargando...</span>
        </div>
        <p class="mt-2">Creando usuario...</p>
      </div>
      <form v-else @submit.prevent="createUser" class="p-3">
        <div class="mb-3">
          <label for="name" class="form-label">Nombre</label>
          <input
            type="text"
            class="form-control"
            id="name"
            v-model="newUser.name"
            required
          />
        </div>
        <div class="mb-3">
          <label for="email" class="form-label">Email</label>
          <input
            type="email"
            class="form-control"
            id="email"
            v-model="newUser.email"
            required
          />
          <div class="email-message-box mt-2">
            <i class="ri-information-line me-1"></i>
            Este email se utilizará para enviar los códigos de acceso al sistema.
          </div>
        </div>
        <div v-if="createError" class="alert alert-danger">
          {{ createError }}
        </div>
        <div class="d-flex justify-content-end">
          <button type="button" class="btn btn-secondary me-2" @click="showCreateModal = false">
            Cancelar
          </button>
          <button type="submit" class="btn btn-success">
            Crear Usuario
          </button>
        </div>
      </form>
    </el-dialog>

    <!-- Modal de confirmación para eliminar usuario -->
    <el-dialog
      v-model="showDeleteModal"
      title="Confirmar Eliminación"
      width="30%"
      class="delete-user-modal"
    >
      <div class="p-3">
        <p class="mb-3">
          ¿Está seguro que desea eliminar al usuario <strong>{{ userToDelete?.name }}</strong>?
        </p>
        <p class="text-danger">
          <i class="ri-error-warning-line me-1"></i>
          Esta acción no se puede deshacer.
        </p>
        <div v-if="deleteError" class="alert alert-danger mt-3">
          {{ deleteError }}
        </div>
        <div class="d-flex justify-content-end mt-4">
          <button type="button" class="btn btn-secondary me-2" @click="showDeleteModal = false">
            Cancelar
          </button>
          <button 
            type="button" 
            class="btn btn-danger" 
            @click="deleteUser" 
            :disabled="deleting"
          >
            <span v-if="deleting" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
            {{ deleting ? 'Eliminando...' : 'Eliminar' }}
          </button>
        </div>
      </div>
    </el-dialog>

    <!-- Modal para gestionar permisos de usuario -->
    <el-dialog
      v-model="showPermissionsModal"
      title="Gestionar Permisos de Usuario"
      width="60%"
      class="permissions-modal"
      style="margin-top: 2vh !important;"
    >
      <div v-if="selectedUser">
        <user-permissions :userId="selectedUser.id" :key="selectedUser.id" />
      </div>
    </el-dialog>
  </div>
</template>

<script>
import axios from "axios";
import UserPermissions from "./UserPermissions.vue";
import UserPermissionsMatrix from "./UserPermissionsMatrix.vue";

export default {
  name: "Users",
  components: {
    UserPermissions,
    UserPermissionsMatrix
  },
  data() {
    return {
      currentView: 'table', // 'table' o 'matrix'
      users: [],
      loading: true,
      error: null,
      // Para crear usuario
      showCreateModal: false,
      creatingUser: false,
      createError: null,
      newUser: {
        name: "",
        email: ""
      },
      // Para eliminar usuario
      showDeleteModal: false,
      userToDelete: null,
      deleting: false,
      deleteError: null,
      // Para gestionar permisos
      showPermissionsModal: false,
      selectedUser: null,
      // Para búsqueda
      searchQuery: ""
    };
  },
  computed: {
    filteredUsers() {
      if (!this.searchQuery || this.searchQuery.trim() === '') {
        return this.users;
      }
      
      const query = this.searchQuery.toLowerCase().trim();
      return this.users.filter(user => {
        const name = user.name ? user.name.toLowerCase() : '';
        const email = user.email ? user.email.toLowerCase() : '';
        return name.includes(query) || email.includes(query);
      });
    }
  },
  mounted() {
    this.fetchUsers();
  },
  methods: {
    async fetchUsers() {
      try {
        this.loading = true;
        
        // Quitamos la autenticación del token para desarrollo
        const apiUrl = process.env.VUE_APP_API_BASE_URL || 'http://teleconnect.test/api/v2';
        
        const response = await axios.get(`${apiUrl}/users`);
        
        // Procesar usuarios para incluir propiedades adicionales
        this.users = response.data.data.map(user => {
          return {
            ...user,
            is_active: user.is_active === 1 || user.is_active === true,
            statusUpdating: false
          };
        });
        
        this.error = null;
      } catch (error) {
        console.error("Error al obtener usuarios:", error);
        this.error = error.response ? 
          `Error: ${error.response.status} - ${error.response.data.message || 'No se pudieron cargar los usuarios'}` : 
          'Error de conexión al servidor';
        this.users = [];
      } finally {
        this.loading = false;
      }
    },
    
    async createUser() {
      try {
        this.creatingUser = true;
        this.createError = null;
        
        const apiUrl = process.env.VUE_APP_API_BASE_URL || 'http://teleconnect.test/api/v2';
        
        // Realizar la petición para crear el usuario
        await axios.post(`${apiUrl}/users`, {
          name: this.newUser.name,
          email: this.newUser.email
        });
        
        // Limpiar el formulario
        this.newUser = {
          name: "",
          email: ""
        };
        
        // Cerrar el modal
        this.showCreateModal = false;
        
        // Recargar la lista de usuarios
        await this.fetchUsers();
        
        // Mostrar mensaje de éxito
        this.$notify({
          title: 'Éxito',
          message: 'Usuario creado correctamente',
          type: 'success',
          duration: 3000
        });
      } catch (error) {
        console.error("Error al crear usuario:", error);
        this.createError = error.response ? 
          (error.response.data.message || 'Error al crear el usuario') : 
          'Error de conexión al servidor';
      } finally {
        this.creatingUser = false;
      }
    },
    
    confirmDelete(user) {
      this.userToDelete = user;
      this.showDeleteModal = true;
      this.deleteError = null;
    },
    
    async deleteUser() {
      if (!this.userToDelete) return;
      
      try {
        this.deleting = true;
        this.deleteError = null;
        
        const apiUrl = process.env.VUE_APP_API_BASE_URL || 'http://teleconnect.test/api/v2';
        
        // Realizar la petición para eliminar el usuario
        await axios.delete(`${apiUrl}/users/${this.userToDelete.id}`);
        
        // Cerrar el modal
        this.showDeleteModal = false;
        this.userToDelete = null;
        
        // Recargar la lista de usuarios
        await this.fetchUsers();
        
        // Mostrar mensaje de éxito
        this.$notify({
          title: 'Éxito',
          message: 'Usuario eliminado correctamente',
          type: 'success',
          duration: 3000
        });
      } catch (error) {
        console.error("Error al eliminar usuario:", error);
        this.deleteError = error.response ? 
          (error.response.data.message || 'Error al eliminar el usuario') : 
          'Error de conexión al servidor';
      } finally {
        this.deleting = false;
      }
    },
    
    /* eslint-disable require-atomic-updates */
    async updateUserStatus(user) {
      // Guardar los valores iniciales
      const userId = user.id;
      const originalStatus = user.is_active;
      
      // Actualizar estado de carga
      user.statusUpdating = true;
      
      try {
        const apiUrl = process.env.VUE_APP_API_BASE_URL || 'http://teleconnect.test/api/v2';
        
        // Realizar la petición para actualizar el estado
        await axios.patch(`${apiUrl}/users/${userId}/status`, {
          is_active: user.is_active
        });
        
        // Mostrar mensaje de éxito
        this.$notify({
          title: 'Éxito',
          message: user.is_active ? 'Usuario activado correctamente' : 'Usuario desactivado correctamente',
          type: 'success',
          duration: 3000
        });
      } catch (error) {
        console.error("Error al actualizar estado del usuario:", error);
        
        // Revertir el cambio en caso de error
        user.is_active = originalStatus;
        
        // Mostrar error
        this.$notify({
          title: 'Error',
          message: 'No se pudo actualizar el estado del usuario',
          type: 'error',
          duration: 3000
        });
      } finally {
        // Quitar el estado de actualización
        user.statusUpdating = false;
      }
    },
    /* eslint-enable require-atomic-updates */
    
    openPermissionsModal(user) {
      this.selectedUser = user;
      this.showPermissionsModal = true;
    },
    
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString(undefined, options);
    },
    
    // Métodos para la búsqueda
    onSearchInput() {
      // Este método se puede usar para debouncing si es necesario en el futuro
      // Por ahora la computed property se actualiza automáticamente
    },
    
    clearSearch() {
      this.searchQuery = "";
    }
  }
};
</script>

<style scoped>
.table th {
  font-size: 0.65rem;
  letter-spacing: 0.05rem;
}

.table td {
  vertical-align: middle;
}

.spinner-border {
  width: 1.5rem;
  height: 1.5rem;
}

.alert {
  font-size: 0.875rem;
}

.create-user-modal .el-dialog,
.delete-user-modal .el-dialog,
.permissions-modal .el-dialog {
  border-radius: 10px;
}

.create-user-modal .el-dialog__header,
.delete-user-modal .el-dialog__header,
.permissions-modal .el-dialog__header {
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
}

.permissions-modal .el-dialog__body {
  padding: 0;
}

.form-control:focus {
  border-color: #5cb85c;
  box-shadow: 0 0 0 0.2rem rgba(92, 184, 92, 0.25);
}

.email-message-box {
  background-color: #f8f9fa;
  border-left: 4px solid #5cb85c;
  padding: 10px;
  margin-top: 8px;
  border-radius: 4px;
  font-size: 0.9rem;
  color: #495057;
  display: flex;
  align-items: center;
}

.email-message-box i {
  font-size: 1.1rem;
  color: #5cb85c;
  margin-right: 8px;
}

/* Estilos para los botones de vista */
.btn-group .btn {
  transition: all 0.2s ease;
}

.btn-group .btn:not(.btn-success):hover {
  background-color: #5cb85c;
  border-color: #5cb85c;
  color: white;
}

/* Estilos para la vista de matriz */
.matrix-view {
  margin: -1rem;
}

/* Estilos para la barra de búsqueda */
.search-input {
  padding-left: 2.5rem;
  padding-right: 2.5rem;
  height: 38px;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  transition: all 0.3s ease;
}

.search-input:focus {
  border-color: #5cb85c;
  box-shadow: 0 0 0 0.2rem rgba(92, 184, 92, 0.25);
  outline: none;
}

.search-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  font-size: 1rem;
  z-index: 2;
  pointer-events: none;
}

.clear-button {
  position: absolute;
  right: 0.25rem;
  top: 50%;
  transform: translateY(-50%);
  width: 30px;
  height: 30px;
  border-radius: 50%;
  padding: 0;
  color: #6c757d;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.clear-button:hover {
  color: #dc3545;
  background-color: #f8f9fa;
}

.clear-button:focus {
  box-shadow: none;
}

/* Estilos para el switch */
:deep(.el-switch__label) {
  font-size: 0.75rem;
}

:deep(.el-switch__label--right) {
  color: #dc3545;
}

:deep(.el-switch__label--left) {
  color: #5cb85c;
}

:deep(.el-switch.is-checked .el-switch__core) {
  border-color: #5cb85c;
  background-color: #5cb85c;
}

:deep(.el-switch .el-switch__core) {
  border-color: #dc3545;
  background-color: #dc3545;
}

/* Estilos para modo oscuro con Glassmorphism */
:global(body.dark-mode) .btn-success {
  background: linear-gradient(135deg, #00ffb3 0%, #00e6a0 100%) !important;
  backdrop-filter: blur(10px) !important;
  border: 1px solid #00ffb3 !important;
  border-radius: 12px !important;
  color: #ffffff !important;
  box-shadow: 0 0 20px rgba(0, 255, 179, 0.3) !important;
  position: relative !important;
  overflow: hidden !important;
}

:global(body.dark-mode) .btn-success::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s ease;
}

:global(body.dark-mode) .btn-success:hover {
  background: linear-gradient(135deg, #00e6a0 0%, #00d4a4 100%) !important;
  backdrop-filter: blur(15px) !important;
  transform: translateY(-2px) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3), 0 0 30px rgba(0, 255, 179, 0.5) !important;
  color: #ffffff !important;
}

:global(body.dark-mode) .btn-success:hover::before {
  left: 100%;
}

:global(body.dark-mode) .btn-outline-success {
  color: #00ffb3 !important;
  border-color: #00ffb3 !important;
}

:global(body.dark-mode) .btn-outline-success:hover {
  background-color: #00ffb3 !important;
  border-color: #00ffb3 !important;
  color: #ffffff !important;
}

:global(body.dark-mode) .spinner-border.text-success {
  color: #00ffb3 !important;
}

:global(body.dark-mode) .text-success {
  color: #00ffb3 !important;
}

:global(body.dark-mode) .form-control:focus {
  border-color: #00ffb3 !important;
  box-shadow: 0 0 0 0.2rem rgba(0, 255, 179, 0.25) !important;
}

:global(body.dark-mode) .email-message-box {
  background-color: #2d2d2d !important;
  border-left-color: #00ffb3 !important;
  color: #ffffff !important;
}

:global(body.dark-mode) .email-message-box i {
  color: #00ffb3 !important;
}

:global(body.dark-mode) .btn-group .btn:not(.btn-success):hover {
  background-color: #00ffb3 !important;
  border-color: #00ffb3 !important;
}

:global(body.dark-mode) :deep(.el-switch__label--left) {
  color: #00eba4 !important;
}

:global(body.dark-mode) :deep(.el-switch.is-checked .el-switch__core) {
  border-color: #00eba4 !important;
  background-color: #00eba4 !important;
}

/* Override específico para el active-color del switch en modo oscuro */
:global(body.dark-mode) :deep(.el-switch[active-color="#5cb85c"].is-checked .el-switch__core) {
  border-color: #00eba4 !important;
  background-color: #00eba4 !important;
}

:global(body.dark-mode) :deep(.el-switch[active-color="#5cb85c"] .el-switch__action) {
  background-color: #ffffff !important;
}

/* Estilos para barra de búsqueda en modo oscuro */
:global(body.dark-mode) .search-input {
  background-color: #2d2d2d !important;
  border-color: #495057 !important;
  color: #ffffff !important;
}

:global(body.dark-mode) .search-input:focus {
  border-color: #00ffb3 !important;
  box-shadow: 0 0 0 0.2rem rgba(0, 255, 179, 0.25) !important;
}

:global(body.dark-mode) .search-input::placeholder {
  color: #adb5bd !important;
}

:global(body.dark-mode) .search-icon {
  color: #adb5bd !important;
}

:global(body.dark-mode) .clear-button {
  color: #adb5bd !important;
}

:global(body.dark-mode) .clear-button:hover {
  color: #ff6b6b !important;
  background-color: #495057 !important;
}

:deep(.el-select__dropdown) {
  z-index: 3000;
}
</style>

<!-- Añadimos estilos globales para los modales -->
<style>
/* Asegurar que los modales aparezcan por encima del menú lateral */
.el-overlay {
  z-index: 9999 !important; /* Valor más alto que cualquier otro elemento */
}

.el-dialog {
  z-index: 10000 !important; /* Debe ser mayor que el del overlay */
}
</style>